/**
 * FinancialStagingArea.js
 * Staging Area tab — manually categorize unrecognized bank transactions.
 * Fetches from /fin-staging and /fin-pnl/categories using API_BASE.
 */
import React, { useEffect, useState } from "react";
import api from "./apiClient";
import { useTheme, getT, glassCard, getBrandColors } from "./ThemeContext";
import FinancialSelect from "./FinancialSelect";
import { ALL_CATEGORIES } from "./constants/financialCategories";
import { useAppConfig } from "./AppContext";
import { useDateTimeFormat } from "./utils/formatters";

function Spinner({ T }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 220 }}>
      <span style={{
        display: "inline-block", width: 36, height: 36, borderRadius: "50%",
        border: "2px solid " + T.divider,
        borderTopColor: T.yellow,
        animation: "fin-spin 0.7s linear infinite",
      }} />
      <style>{`@keyframes fin-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function SortTh({ label, col, sortKey, sortDir, onSort, style = {} }) {
  const active = sortKey === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        padding: "11px 14px", fontWeight: 800,
        fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.10em",
        color: active ? getBrandColors().yellow : undefined,
        borderBottom: undefined,
        cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
        ...style,
      }}
    >
      {label}
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.3, fontSize: 10 }}>
        {active ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
      </span>
    </th>
  );
}

export default function FinancialStagingArea({ onApproved }) {
  const { isDark } = useTheme();
  const T  = getT(isDark);
  const gc = glassCard(T);
  const { config } = useAppConfig();
  const { fmtDate } = useDateTimeFormat();
  const fmt = (n) =>
    Number(n).toLocaleString("en-US", { style: "currency", currency: config.currency || "USD" });

  const [items,      setItems]      = useState([]);
  const [categories, setCategories] = useState(ALL_CATEGORIES);
  const [selections, setSelections] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message,    setMessage]    = useState(null);
  const [sortKey,    setSortKey]    = useState("date");
  const [sortDir,    setSortDir]    = useState("asc");

  const handleSort = (col) => {
    setSortDir(prev => (sortKey === col ? (prev === "asc" ? "desc" : "asc") : "asc"));
    setSortKey(col);
  };

  const sortedItems = [...items].sort((a, b) => {
    let av, bv;
    if (sortKey === "date")   { av = a.transaction_date; bv = b.transaction_date; }
    else if (sortKey === "bank")   { av = (a.bank_source || "").toLowerCase(); bv = (b.bank_source || "").toLowerCase(); }
    else if (sortKey === "amount") { av = Number(a.amount); bv = Number(b.amount); }
    else if (sortKey === "description") { av = (a.description || "").toLowerCase(); bv = (b.description || "").toLowerCase(); }
    else if (sortKey === "type")   { av = (a.income_expense || "").toLowerCase(); bv = (b.income_expense || "").toLowerCase(); }
    else if (sortKey === "category") { av = (a.suggested_category || "").toLowerCase(); bv = (b.suggested_category || "").toLowerCase(); }
    else return 0;
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get("/fin-staging"),
      api.get("/fin-pnl/categories"),
    ])
      .then(([s, c]) => {
        setItems(s.data);
        setCategories([...new Set([...ALL_CATEGORIES, ...c.data])].sort());
        const init = {};
        s.data.forEach(item => {
          init[item.id] = {
            category:       item.suggested_category || "",
            income_expense: item.income_expense,
            save_rule:      false,
          };
        });
        setSelections(init);
      })
      .catch(e => setMessage({ type: "error", msg: e.message }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (id, field, value) =>
    setSelections(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const approveOne = async (id) => {
    const sel = selections[id];
    if (!sel?.category) {
      setMessage({ type: "error", msg: "Please select a category first." });
      return;
    }
    try {
      await api.post("/fin-staging/" + id + "/categorize", {
        category:       sel.category,
        income_expense: sel.income_expense,
        save_rule:      sel.save_rule,
      });
      setItems(prev => prev.filter(i => i.id !== id));
      onApproved?.();
    } catch (e) {
      setMessage({ type: "error", msg: e.response?.data?.detail || e.message });
    }
  };

  const approveAll = async () => {
    const payload = items
      .map(item => {
        const sel = selections[item.id];
        if (!sel?.category) return null;
        return { id: item.id, category: sel.category, income_expense: sel.income_expense };
      })
      .filter(Boolean);

    if (!payload.length) {
      setMessage({ type: "error", msg: "No items with a selected category to approve." });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/fin-staging/bulk-categorize", payload);
      setMessage({ type: "success", msg: payload.length + " transactions approved." });
      loadData();
      onApproved?.();
    } catch (e) {
      setMessage({ type: "error", msg: e.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const selectStyle = {
    background: T.inputBg, border: "1px solid " + T.inputBdr,
    borderRadius: 7, color: T.inputText,
    fontSize: 12, padding: "3px 8px", outline: "none", cursor: "pointer",
    appearance: "none", WebkitAppearance: "none",
    colorScheme: T.isDark ? "dark" : "light",
  };

  const thStyle = {
    padding: "11px 14px", fontWeight: 800,
    fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.10em",
    color: T.textMuted, borderBottom: "1px solid " + T.divider,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: T.textPrimary }}>
            Staging Area
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: T.textMuted }}>
            {items.length} transaction{items.length !== 1 ? "s" : ""} need manual categorization
          </p>
        </div>
        {items.length > 0 && (
          <button
            onClick={approveAll}
            disabled={submitting}
            style={{
              fontSize: 13, fontWeight: 700, padding: "8px 18px", borderRadius: 10,
              background: T.isDark ? "rgba(247,206,11,0.15)" : "rgba(247,206,11,0.20)",
              border: "1px solid " + T.yellow + "55",
              color: T.accentText, cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.5 : 1, fontFamily: "inherit",
            }}
          >
            {submitting ? "Approving…" : "Approve All Selected"}
          </button>
        )}
      </div>

      {/* Status message */}
      {message && (
        <div style={{
          padding: "10px 14px", borderRadius: 10, fontSize: 12, fontWeight: 600,
          color:       message.type === "success" ? T.green  : T.red,
          background:  message.type === "success" ? T.greenBg : T.redBg,
          border: "1px solid " + (message.type === "success" ? T.green : T.red) + "44",
        }}>
          {message.msg}
        </div>
      )}

      {loading ? <Spinner T={T} /> : items.length === 0 ? (
        <div style={{ ...gc, padding: 56, textAlign: "center", color: T.textMuted }}>
          No pending transactions in staging area.
        </div>
      ) : (
        <div style={{ ...gc, borderRadius: 16, overflowX: "auto" }}>
          <table style={{ minWidth: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid " + T.divider }}>
                <SortTh label="Date"        col="date"        sortKey={sortKey} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "left" }} />
                <SortTh label="Description" col="description" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "left" }} />
                <SortTh label="Bank"        col="bank"        sortKey={sortKey} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "left" }} />
                <SortTh label="Amount"      col="amount"      sortKey={sortKey} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "right" }} />
                <SortTh label="Type"        col="type"        sortKey={sortKey} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "left" }} />
                <SortTh label="Category"    col="category"    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} style={{ textAlign: "left" }} />
                <th style={{ ...thStyle, textAlign: "center" }}>Save Rule</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map(item => {
                const sel = selections[item.id] || {};
                return (
                  <tr key={item.id}
                    style={{ borderBottom: "1px solid " + T.divider, transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.glass}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "9px 14px", whiteSpace: "nowrap", color: T.textMuted }}>
                      {fmtDate(item.transaction_date)}
                    </td>
                    <td style={{
                      padding: "9px 14px", color: T.textPrimary,
                      maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }} title={item.description}>
                      {item.description}
                    </td>
                    <td style={{ padding: "9px 14px", color: T.textMuted }}>{item.bank_source || "—"}</td>
                    <td style={{
                      padding: "9px 14px", textAlign: "right", fontWeight: 700,
                      color: Number(item.amount) >= 0 ? T.green : T.textSub,
                      fontVariantNumeric: "tabular-nums",
                    }}>
                      {fmt(item.amount)}
                    </td>
                    <td style={{ padding: "9px 14px" }}>
                      <FinancialSelect
                        value={sel.income_expense || item.income_expense}
                        onChange={v => update(item.id, "income_expense", v)}
                        options={["Income","Expense","Equity"]}
                        T={T}
                        style={{ minWidth: 100 }}
                      />
                    </td>
                    <td style={{ padding: "9px 14px" }}>
                      <FinancialSelect
                        value={sel.category || ""}
                        onChange={v => update(item.id, "category", v)}
                        options={categories}
                        placeholder="— select —"
                        T={T}
                        style={{ minWidth: 180 }}
                      />
                    </td>
                    <td style={{ padding: "9px 14px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={!!sel.save_rule}
                        onChange={e => update(item.id, "save_rule", e.target.checked)}
                        title="Save as a new categorization rule for future uploads"
                        style={{ accentColor: T.yellow, width: 14, height: 14, cursor: "pointer" }}
                      />
                    </td>
                    <td style={{ padding: "9px 14px", textAlign: "center" }}>
                      <button
                        onClick={() => approveOne(item.id)}
                        style={{
                          fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 7,
                          background: T.isDark ? "rgba(247,206,11,0.13)" : "rgba(247,206,11,0.18)",
                          border: "1px solid " + T.yellow + "44",
                          color: T.accentText, cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        Approve
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
